function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { useMemo, useState } from 'react';
import { compact, keyBy } from 'lodash-es';
import { pathgen } from 'object-path-generator';
import { Box, Column, Divider, Row, Typography, WithInfoIcon, useOverlayContext, List } from 'seagull';
import { generateUniqueId } from 'seagull/utils/generateUniqueId';
import EditServiceContentModal from "../EditServiceContentModal/EditServiceContentModal";
import { useStore } from "../../contexts";
import { useActiveServiceTemplates } from "../../hooks/reactQuery/queries/useActiveServiceTemplates";
import { serviceCostModel, serviceModel } from "../../models";
import { selectors } from "../../store/state-selectors";
import AddServicesFilterPicker from "./AddServicesFilterPicker";
import styles from "./IncludedServices.module.scss";
import ServiceItem from "./ServiceItem";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export function applyServicesSelection(currentServices, selectedServiceTemplateIds, serviceTemplatesMap) {
  const remainingIncludedServices = currentServices.filter(s => s.serviceTemplateId && selectedServiceTemplateIds.includes(s.serviceTemplateId));
  const newServiceTemplateIds = selectedServiceTemplateIds.filter(id => !remainingIncludedServices.some(s => s.serviceTemplateId === id));
  const newIncludedServices = compact(newServiceTemplateIds.map(serviceTemplateId => {
    const template = serviceTemplatesMap[serviceTemplateId];
    if (!template) return null;
    const includedService = {
      id: generateUniqueId('service-bundle'),
      serviceTemplateId,
      name: template.name,
      description: template.description,
      rate: serviceCostModel.finalPrice(template.cost, 1),
      integrations: template.integrations
    };
    return includedService;
  }));
  return remainingIncludedServices.concat(newIncludedServices);
}
export const IncludedServicesDataTestIds = pathgen('IncludedServicesDataTestIds');
const IncludedServices = props => {
  const {
    mode,
    value,
    showErrors = false,
    tooltip,
    onChange
  } = props;
  const isReadOnly = mode === 'readonly';
  const {
    showModal
  } = useOverlayContext();
  const {
    serviceTemplates
  } = useActiveServiceTemplates();
  const [selectedTemplateIds, setSelectedTemplateIds] = useState(getInitialSelectedTemplateIds);
  const isInvalid = serviceModel.validateBundleIncludedServices(value).length > 0;
  const serviceTemplatesMap = useMemo(() => keyBy(serviceTemplates, 'id'), [serviceTemplates]);
  const isVendor = useStore(selectors.user.isVendor);
  const sortedServiceTemplates = useMemo(() => {
    const selectedServiceTemplates = serviceTemplates === null || serviceTemplates === void 0 ? void 0 : serviceTemplates.filter(serviceTemplate => value.some(service => service.serviceTemplateId === serviceTemplate.id));
    const unselectedServiceTemplates = serviceTemplates === null || serviceTemplates === void 0 ? void 0 : serviceTemplates.filter(serviceTemplate => !(selectedServiceTemplates !== null && selectedServiceTemplates !== void 0 && selectedServiceTemplates.includes(serviceTemplate)));
    return [...(selectedServiceTemplates || []), ...(unselectedServiceTemplates || [])];
  }, [serviceTemplates, value]);
  function handleApplyServices(selectedServiceTemplateIds) {
    const newServices = applyServicesSelection(value, selectedServiceTemplateIds, serviceTemplatesMap);
    setTimeout(() => onChange(newServices), 150);
  }
  function handleDelete(includedService) {
    setSelectedTemplateIds(selectedServiceTemplateIds => selectedServiceTemplateIds.filter(serviceTemplateId => serviceTemplateId !== includedService.serviceTemplateId));
    onChange(value.filter(service => service.serviceTemplateId !== includedService.serviceTemplateId));
  }
  function handleEdit(includedService) {
    showModal({
      closeOnClickOutside: true,
      closeOnEscapeKey: true,
      content: _ref => {
        let {
          dismissModal
        } = _ref;
        return /*#__PURE__*/_jsx(EditServiceContentModal, {
          title: "Edit Included Service",
          providedServiceName: includedService.name,
          providedServiceDescription: includedService.description,
          dismissModal: dismissModal,
          onSave: _ref2 => {
            let {
              name,
              description
            } = _ref2;
            onChange(value.map(service => service.serviceTemplateId === includedService.serviceTemplateId ? _objectSpread(_objectSpread({}, service), {}, {
              name,
              description
            }) : service));
            dismissModal();
          }
        });
      }
    });
  }
  function handleServiceToggle(templateId) {
    setSelectedTemplateIds(prev => prev.includes(templateId) ? prev.filter(id => id !== templateId) : [...prev, templateId]);
  }
  function getInitialSelectedTemplateIds() {
    return compact(value.map(service => service.serviceTemplateId));
  }
  return /*#__PURE__*/_jsxs(Column, {
    "data-testid": IncludedServicesDataTestIds.Root(),
    className: styles.root,
    children: [/*#__PURE__*/_jsx(Row, {
      className: styles.includedServicesHeader,
      "data-testid": IncludedServicesDataTestIds.IncludedServicesHeader(),
      children: /*#__PURE__*/_jsx(WithInfoIcon, {
        info: tooltip,
        showInfo: !isReadOnly,
        children: /*#__PURE__*/_jsxs(Typography, {
          weight: "normal",
          children: ["Included Services", isReadOnly ? ':' : '']
        })
      })
    }), value.length > 0 && /*#__PURE__*/_jsx(List, {
      className: styles.servicesWrapper,
      style: {
        listStyleType: 'disc',
        listStylePosition: 'inside',
        padding: 0
      },
      children: value.map((includedService, index) => /*#__PURE__*/_jsx(ServiceItem, {
        mode: mode,
        isArchived: isVendor && !!serviceTemplates && !!includedService.serviceTemplateId && !serviceTemplatesMap[includedService.serviceTemplateId],
        includedService: includedService,
        testId: IncludedServicesDataTestIds.ServiceItem(includedService.serviceTemplateId || ''),
        onDelete: handleDelete,
        onEdit: handleEdit
      }, includedService.serviceTemplateId || index))
    }), !isReadOnly && /*#__PURE__*/_jsx(Box, {
      className: styles.addServices,
      children: /*#__PURE__*/_jsx(AddServicesFilterPicker, {
        hasError: showErrors && isInvalid,
        serviceTemplates: sortedServiceTemplates,
        selectedServiceTemplateIds: selectedTemplateIds,
        testId: IncludedServicesDataTestIds.AddServices(),
        onApply: handleApplyServices,
        onToggle: handleServiceToggle,
        onClose: _ref3 => {
          let {
            revert
          } = _ref3;
          if (revert) {
            setSelectedTemplateIds(getInitialSelectedTemplateIds());
          }
        }
      })
    }), isReadOnly && /*#__PURE__*/_jsx(Divider, {
      margin: 4
    })]
  });
};
export default IncludedServices;